import { DateOptions, IConfiguredTrafficReport } from "./IConfiguredTrafficReport";
import { AggregatedTargetAttribute, AggregationPeriod, getLaneDisplayName, getTargetTypeDisplayName, rawPost, Reports, TrafficReportConfiguration } from "@app/shared";
import { getConfiguredLaneName } from "@app/shared/src/models/Shared.Lane.helpers";
import { TotalVolumeChartProps, TotalVolumeDataLoaderProps } from "./TotalVolumeDataLoader";
import { useContext, useEffect } from "react";
import { colorMap, ReportContext } from "./reportHelpers";
import { formatInTimeZone } from "date-fns-tz";
import { Bar, ChartProps } from "react-chartjs-2";
import { useFetch } from "../../hooks";
import { ReportLoadingErrorWrapper } from "./ReportLoadingErrorWrapper";


export const TotalVolumeLanesAndTypes: React.FC<TotalVolumeChartProps> = (reportProps) => {
    const { size, data, chartTimezone, title, subTitle } = reportProps;
    const { reportConfig } = useContext(ReportContext);
    const facilityLaneLines = reportConfig?.context?.facility?.laneLines;
    const laneLabel = (lane: number) => {
        const decoded = getConfiguredLaneName(lane, facilityLaneLines);
        const raw = getLaneDisplayName(lane);
        return decoded?.hasConfiguredName ? `${decoded.primary} (${raw})` : raw;
    };
    //const { numberFormatter } = useAuth();
    const timePeriods = [...new Set(data.map(d => d.periodStart!.toISOString()))].sort();
    const lanes = [...new Set(data.map(d => d.inCount))].sort();
    const ttValues = [...new Set(data.map(d => d.outCount))].sort();

    // 2. Create one dataset per TT value (for stacking)
    const datasets = ttValues.flatMap(tt => lanes.map(lane => ({
        label: `${laneLabel(lane)} - ${getTargetTypeDisplayName(tt)}`,
        data: timePeriods.map(time =>
            data.find(d =>
                d.periodStart!.toISOString() === time &&
                d.inCount === lane &&
                d.outCount === tt
            )?.count || 0),
        backgroundColor: colorMap[AggregatedTargetAttribute.TargetType][tt],
        stack: `${lane}`, // Same stack group for all
    })));

    const chartjsProps = {
        data:
        {
            labels: timePeriods.map(x => formatInTimeZone(x, chartTimezone, 'MMM dd HH:mm')),
            datasets
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                title: {
                    display: !!title,
                    text: !!!title ? undefined : typeof title !== 'string' ? title(data) : title,
                    font: {
                        size: 24, // Font size for the main title
                        weight: 'bold'
                    },
                },
                subtitle: {
                    display: !!subTitle,
                    text: !!!subTitle ? undefined : typeof subTitle !== 'string' ? subTitle(data) : subTitle,
                    font: {
                        size: 16, // Font size for the main title
                        weight: 'bold'
                    },
                },
                legend: {
                    display: true,
                    position: 'bottom',
                    labels: {
                        generateLabels: () => {
                            return ttValues.map(tt => ({
                                text: getTargetTypeDisplayName(tt),
                                fillStyle: colorMap[AggregatedTargetAttribute.TargetType][tt],
                                strokeStyle: colorMap[AggregatedTargetAttribute.TargetType][tt]
                            }))
                        }
                    }
                },
            },
            interaction: {
                mode: 'index',
                intersect: false
            },
            scales: {
                y: {
                    beginAtZero: true,
                    stacked: true
                },
                x: {
                    stacked: true
                }
            }
        }
    } as Omit<ChartProps<"bar", (number | [number, number] | null)[], unknown>, "type">;

    return (
        <div style={{ position: 'relative', width: size.width, height: size.height ?? 1100 }}>
            <Bar {...chartjsProps as ChartProps<'bar'>} ></Bar>
        </div>
    );
}

export const TargetTypeByLaneDataLoader: React.FC<TotalVolumeDataLoaderProps> = (props) => {
    const { searchParams, reportConfig, setIsLoading } = useContext(ReportContext);
    const [data, error, { isLoading }] = useFetch(() => Reports.getTotalTypesByLanes(AggregatedTargetAttribute.Lane, AggregatedTargetAttribute.TargetType, props.aggregationPeriod, searchParams), [searchParams]);
    useEffect(() => setIsLoading(isLoading), [isLoading, setIsLoading]);
    if (reportConfig.chartType !== 'bar') {
        throw new Error('unsupported chart type for total volume by lane and target type');
    }
    return (<ReportLoadingErrorWrapper error={error} hasNoData={!!!data?.length} isLoading={isLoading} size={props.size} >
        <TotalVolumeLanesAndTypes {...props} grouping={reportConfig.grouping} data={data!} startDate={searchParams.inclusiveStart} endDate={searchParams.exclusiveEnd} chartTimezone={reportConfig.chartTimezone} chartType={reportConfig.chartType} />
    </ReportLoadingErrorWrapper>);
}


export const TargetTypeByLane: IConfiguredTrafficReport = {
    name: "Target Types By Lane",
    key: "tvttbl5",
    description: "This report shows target types in each lane for the specified period",
    component: props => (<TargetTypeByLaneDataLoader {...props} aggregationPeriod={AggregationPeriod.Day} />),
    defaultChartType: 'bar',
    defaultRange: DateOptions.LastWeek,
    downloadData: opts => downloadCSV(AggregationPeriod.FiveMinutes, opts),
    config: {
        requiresMonth: false,
        allowsCustomRange: true,
        allowTargetTypeGrouping: false,
        disallowChartTypeToggling: true,
        requiresYear: false
    }
};

const downloadCSV = async (aggPeriod: AggregationPeriod, opts: TrafficReportConfiguration) => {

    try {
        const response = await rawPost(`/api/report/total-volume/by-${AggregatedTargetAttribute.Lane}/by-${AggregatedTargetAttribute.TargetType}/by-${AggregationPeriod.FiveMinutes}?csv`,
            JSON.stringify(opts),
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/octet-stream'
                }
            });
        const blob = await response.blob();
        if (!response.ok) {
            console.error(response.statusText);
            return;
        }

        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);

        // Extract the file name from the response headers
        const contentDispositionHeader = response.headers.get('content-disposition');
        const fileNameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = fileNameRegex.exec(contentDispositionHeader || '');
        const fileName = matches && matches[1] ? matches[1] : 'VolumeReport.xlsx';

        link.download = fileName;
        link.click();
        link.remove();
        window.URL.revokeObjectURL(link.href)

    } catch (error) {
        console.error('Download failed:', error);
    }
}